import React from "react";
import { Input, Button } from "antd";
import styled from "styled-components";
import { PlusOutlined } from "@ant-design/icons";
import { Icon } from "@/shared/components/Icon/Icon";

interface Props {
	searchText: string;
	hasActiveFilters: boolean;
	onSearchChange: (value: string) => void;
	onClearFilters: () => void;
	onNewCampaign: () => void;
	showNewCampaign?: boolean;
	extraPills?: Array<{
		key: string;
		label: string;
		onRemove: () => void;
	}>;
}

const FiltersRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 20px;
`;

const FiltersLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
`;

const FiltersRight = styled.div`
	display: flex;
	align-items: flex-start;
	align-self: flex-start;
`;

const SearchInput = styled(Input)`
	width: 320px;

	&& {
		border-radius: 8px;
		height: 40px;
		font-size: 16px;
	}
`;

const ClearFiltersAction = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border: none;
	background: transparent;
	border-radius: 999px;
	font-size: 14px;
	color: #33363d;
	cursor: pointer;
	padding: 8px 12px;
	transition: all 0.2s ease;

	svg {
		color: inherit;
		transition: color 0.2s ease;
	}

	.anticon {
		font-size: 22px;
		color: #33363d;
		transition: color 0.2s ease;
	}

	&:hover {
		color: #1f2430;
		background: #ffffff;
		box-shadow: 0 6px 14px rgba(43, 52, 89, 0.14);
	}

	&:active {
		background: #efefef;

		svg {
			color: #f72586;
		}
	}

	&:disabled {
		color: #98a0b5;
		cursor: not-allowed;
		opacity: 0.6;
		box-shadow: none;
	}

	&:disabled svg {
		color: inherit;
	}

	&:disabled:hover {
		background: transparent;
		color: #98a0b5;
		box-shadow: none;
	}
`;

const NewCampaignButton = styled(Button)`
	&& {
		background: #3a0ca3;
		border-color: #3a0ca3;
		border-radius: 64px;
		height: 40px;
		font-weight: 600;
		padding-inline: 24px;
		font-size: 16px;
	}
`;

const FilterPill = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	max-width: 160px;
	padding: 8px 14px;
	border-radius: 999px;
	background: #ebeefd;
	color: #122e99;
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease;

	&:hover {
		background: #cfd6f1;
		box-shadow: 0 6px 14px rgba(43, 52, 89, 0.1);
	}
`;

const FilterPillText = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const RemovePillButton = styled.button`
	border: none;
	background: transparent;
	color: #8b98dd;
	font-size: 28px;
	line-height: 0.7;
	cursor: pointer;
	padding: 0;
	transition: color 0.2s ease;

	&:hover {
		color: #5f73d8;
	}

	&:active {
		color: #f72586;
	}
`;

export const CampaignFilters: React.FC<Props> = ({
	searchText,
	hasActiveFilters,
	onSearchChange,
	onClearFilters,
	onNewCampaign,
	showNewCampaign = true,
	extraPills = [],
}) => {
	const trimmedSearch = searchText.trim();
	const searchPillLabel = `Search: ${trimmedSearch}`;

	return (
		<FiltersRow>
			<FiltersLeft>
				<SearchInput
					placeholder="Search Campaigns"
					suffix={<Icon name="menuSearch" size={24} />}
					value={searchText}
					onChange={(e) => onSearchChange(e.target.value)}
				/>

				{(extraPills.length > 0 || trimmedSearch !== "") && (
					<ClearFiltersAction onClick={onClearFilters} disabled={!hasActiveFilters}>
						<Icon name="clearFilters" size={24} />
						<span>Clear Filters</span>
					</ClearFiltersAction>
				)}

				{trimmedSearch && (
					<FilterPill>
						<FilterPillText title={searchPillLabel}>{searchPillLabel}</FilterPillText>
						<RemovePillButton onClick={() => onSearchChange("")} aria-label="Remove search filter">
							×
						</RemovePillButton>
					</FilterPill>
				)}

				{extraPills.map((pill) => (
					<FilterPill key={pill.key}>
						<FilterPillText title={pill.label}>{pill.label}</FilterPillText>
						<RemovePillButton onClick={pill.onRemove} aria-label={`Remove ${pill.label}`}>
							×
						</RemovePillButton>
					</FilterPill>
				))}
			</FiltersLeft>

			<FiltersRight>
				{showNewCampaign && (
					<NewCampaignButton
						type="primary"
						icon={
							<PlusOutlined
								style={{
									fontSize: 14,
									stroke: "currentColor",
									strokeWidth: 40,
									border: 2,
									borderColor: "#FFFFFF",
								}}
							/>
						}
						onClick={onNewCampaign}
					>
						New Campaign
					</NewCampaignButton>
				)}
			</FiltersRight>
		</FiltersRow>
	);
};
